const selectors = {
  tolstoyWrapper: '.tolstoy-carousel-videos-container',
  tolstoyItem: '[data-tolstoy-element*="tolstoy-carousel-tile-container"]',
  tolstoyArrow: '.tolstoy-product-tile-arrow',
  tolstoyContent: '.tolstoy-product-content',
  tolstoyMuteVideo: '.tolstoy-carousel-mute-button',
}

const classes = {
  swiperContainer: 'tolstoy-swiper-container',
  swiperWrapper: 'swiper-wrapper',
  swiperSlide: 'swiper-slide',
  swiperScrollbar: 'swiper-scrollbar',
  hidden: 'tolstoy-hidden',
  active: 'is-active',
  hasSound: 'has-sound',
}

class tolstoyCarousel extends HTMLElement {
  constructor() {
    super()

    this.observer = new MutationObserver(this.handleDOMChanges.bind(this))
    this.swiper = null
    this.initialized = false
  }

  connectedCallback() {
    this.observeChanges()
  }

  observeChanges() {
    this.observer.observe(this, {
      childList: true,
      subtree: true
    })

    // Also immediately check if tolstoy is already present
    setTimeout(() => {
      const existingWrapper = this.querySelector(selectors.tolstoyWrapper)
      if (existingWrapper) {
        this.handleDOMChanges([{type: 'childList'}])
      }
    }, 100)
  }

  handleDOMChanges(mutations) {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        const tolstoyWrapper = this.querySelector(selectors.tolstoyWrapper)

        if (tolstoyWrapper && !this.initialized) {
          this.initializeSwiper(tolstoyWrapper)
          this.initialized = true
        }
      }
    })
  }

  initializeSwiper(tolstoyWrapper) {
    const tolstoyItems = tolstoyWrapper.querySelectorAll(selectors.tolstoyItem)

    if (tolstoyItems.length === 0) {
      return
    }

    tolstoyWrapper.classList.add(classes.hidden)

    // Create Swiper container
    const swiperContainer = this.createSwiperContainer()
    const swiperWrapper = swiperContainer.querySelector(`.${classes.swiperWrapper}`)
    let heightItems = Infinity
    // Clone and add items to Swiper
    tolstoyItems.forEach((item) => {
      const tolstoyVideoContainer = item.querySelector('.tolstoy-carousel-video-container')
      const height = parseFloat(tolstoyVideoContainer.style.getPropertyValue('height'))
      if (height > 0 && height < heightItems) {
        heightItems = height
      }
      const slide = this.createSwiperSlide(item, heightItems)
      swiperWrapper.appendChild(slide)
    })

    // Insert Swiper after Tolstoy carousel
    this.appendChild(swiperContainer)

    // Initialize Swiper
    this.loadSwiperFromCDN(swiperContainer)
  }

  createSwiperContainer() {
    const container = document.createElement('div')
    container.className = classes.swiperContainer

    const wrapper = document.createElement('div')
    wrapper.className = classes.swiperWrapper

    const scrollbar = document.createElement('div')
    scrollbar.className = classes.swiperScrollbar

    container.appendChild(wrapper)
    container.appendChild(scrollbar)

    return container
  }

  createSwiperSlide(tolstoyItem, heightItem) {
    const slide = document.createElement('div')
    slide.className = classes.swiperSlide

    const clonedItem = tolstoyItem.cloneNode(true)

    const stylesToRemove = [
      'transform',
      'position',
      'left',
      'right',
      'top',
      'bottom',
      'margin-left',
      'margin-right',
      'translate',
      'translateX',
      'translateY',
      'flex',
      'display',
      'justify-content',
      'align-items',
      'transition',
    ]

    const tolstoyVideoContainer = clonedItem.querySelector('.tolstoy-carousel-video-container')

    stylesToRemove.forEach(prop => {
      clonedItem.style.removeProperty(prop)
    })

    tolstoyVideoContainer.style.height = heightItem + 'px'
    tolstoyVideoContainer.style.removeProperty('min-height')
    clonedItem.querySelector('.tolstoy-carousel-tile')?.style.removeProperty('height')
    this.handleClickArrow(clonedItem)
    this.handleClickMuted(clonedItem)
    slide.appendChild(clonedItem)

    return slide
  }

  loadSwiperFromCDN(container) {
    // Load Swiper JS
    import('https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.mjs').then(function (swiperModule) {
      const Swiper = swiperModule.Swiper || swiperModule.default

      this.createSwiper(container, Swiper)
    }.bind(this)).catch(error => {
      this.createFallbackCarousel(container)
    })
  }

  createFallbackCarousel(container) {
    container.style.overflowX = 'auto'
    container.style.display = 'flex'
    container.style.scrollBehavior = 'smooth'

    const wrapper = container.querySelector('.swiper-wrapper')
    if (wrapper) {
      wrapper.style.display = 'flex'

      const slides = wrapper.querySelectorAll('.swiper-slide')
      slides.forEach(slide => {
        slide.style.flexShrink = '0'
        slide.style.width = 'auto'
      })
    }
  }

  createSwiper(container, Swiper) {
    try {
      this.swiper = new Swiper(container, {
        direction: 'horizontal',
        loop: true,
        slidesPerView: 'auto',
        centeredSlides: true,
        grabCursor: true,
        watchOverflow: true,
        mousewheel: {
          forceToAxis: true
        },
        scrollbar: {
          el: `.${classes.swiperScrollbar}`,
          draggable: true
        },
        on: {
          slideChange: (el) => {
            this.handleVideoPlayback(el)
          },
          init: (el) => {
            this.handleVideoPlayback(el)
          }
        }
      })

    } catch (error) {
      this.createFallbackCarousel(container)
    }
  }

  handleVideoPlayback(el) {
    if (!el) return

    const slides = el.slides

    slides.forEach((slide, index) => {
      const videos = slide.querySelectorAll('video')

      if (videos.length === 0) return

      if (index === el.activeIndex) {
        videos.forEach(video => {
          if (video.paused) {
            if (video.hasAttribute('autoplay') || video.muted) {
              video.play().catch(e => {
              })
            }
          }
        })
      } else {
        videos.forEach(video => {
          if (!video.paused) {
            video.pause()

            video.currentTime = 0
          }
        })
      }
    })
  }

  handleClickMuted(container) {
    const videos = container.querySelectorAll('video')
    const tolstoyMuteVideoEl = container.querySelector(selectors.tolstoyMuteVideo)

    if (tolstoyMuteVideoEl) {
      tolstoyMuteVideoEl.addEventListener('click', () => {
        videos.forEach(video => {
          video.muted = !video.muted
        })

        tolstoyMuteVideoEl.classList.toggle(classes.hasSound)
      })
    }
  }

  handleClickArrow(container) {
    const tolstoyArrowEl = container.querySelector(selectors.tolstoyArrow)
    const tolstoyContentEl = container.querySelector(selectors.tolstoyContent)

    if (tolstoyArrowEl && tolstoyContentEl) {
      tolstoyArrowEl.addEventListener('click', () => {
        if (!tolstoyArrowEl.classList.contains(classes.active)) {
          tolstoyArrowEl.style.rotate = '180deg'
          tolstoyArrowEl.style.right = '0'

          tolstoyContentEl.style.position = 'absolute'
          tolstoyContentEl.style.overflow = 'hidden scroll'
          tolstoyContentEl.style.transition = 'min-height 0.3s, max-height 0.3s'
          tolstoyContentEl.style.minHeight = '100%'
          tolstoyContentEl.style.maxHeight = '100%'
          tolstoyContentEl.style.paddingRight = '29px'
        } else {
          tolstoyArrowEl.style.removeProperty('transform')
          tolstoyArrowEl.style.right = '15px'

          tolstoyContentEl.style.position = 'relative'
          tolstoyContentEl.style.overflow = 'hidden'
          tolstoyContentEl.style.transition = 'min-height 0.3s ease, max-height 0.3s ease'
          tolstoyContentEl.style.minHeight = '80px'
          tolstoyContentEl.style.maxHeight = '80px'
          tolstoyContentEl.style.removeProperty('padding-right')
        }

        tolstoyArrowEl.classList.toggle(classes.active)
      })
    }
  }

  disconnectedCallback() {
    this.observer.disconnect()

    if (this.swiper) {
      this.swiper.destroy(true, true)
      this.swiper = null
    }
  }
}

customElements.define('tolstoy-carousel', tolstoyCarousel)